/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import * as React from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { LFC } from '../../Legacy';
import { Emoji } from './Emoji';
import { EmojiData, EmojiDiversifications } from './EmojiModel';
import { EmojiStore } from './EmojiStore';
import './emoji-picker.css';
import './emoji.css';
import { memoizeOne } from './memoize';

const XScrollbars = Scrollbars as any;

export type EmojiPickerProps = { width?: number, height?: number, onSelect: (emoji: EmojiData) => void, diversityHandler?: EmojiPickerDiversityPersistence };
export type EmojiPickerState = { offset: number, diversity: string, preview?: string, search?: string };

export interface EmojiPickerDiversityPersistence {
    get(): string;
    set(to: string): void;
}

const LocalStorageEmojiPickerDiversityPersistence: EmojiPickerDiversityPersistence = {
    get: () => JSON.parse(localStorage.getItem('deqo.ui.emojiDiversity') ?? 'null'),
    set: x => localStorage.setItem('deqo.ui.emojiDiversity', JSON.stringify(x)),
};

export class EmojiPicker extends React.Component<EmojiPickerProps, EmojiPickerState> {

    static defaultProps = {
        width: 300,
        height: 295,
        diversityHandler: LocalStorageEmojiPickerDiversityPersistence,
    };

    private scrollbars: any = null;

    private getContents = memoizeOne(computeContents);
    private getDimensions = memoizeOne(computeDimensions);
    private getLayout = memoizeOne(computeLayout);
    private getCategory = memoizeOne(computeCategory);

    private get derivedState() {
        const state = this.state;
        const contents = this.getContents(state.diversity, state.search);
        const dimensions = this.getDimensions(this.props.width!, this.props.height!);
        const layout = this.getLayout(contents, dimensions);
        const category = this.getCategory(state.offset, layout);
        return { contents, dimensions, layout, category };
    }

    constructor(props: EmojiPickerProps) {
        super(props);
        this.state = { offset: 0, diversity: props.diversityHandler!.get() || EmojiDiversifications[0].code };
    }

    public render() {
        const { state, derivedState } = this;
        const { dimensions, layout, category } = derivedState;

        const categories = EmojiStore.getCategories();
        const diversities = EmojiDiversifications;

        const scrollerStyling = {
            width: dimensions.width,
            height: dimensions.height,
        };

        const gridStyling = {
            position: 'relative' as any,
            marginLeft: dimensions.margin,
            height: layout.height,
        };

        const cellStyling = {
            position: 'relative' as any,
            width: layout.cellWidth,
            height: layout.cellWidth,
            padding: layout.cellPadding,
            marginLeft: layout.cellMargin,
            marginRight: layout.cellMargin,
        };

        const bottom = state.offset + dimensions.height;
        const blocks = layout.blocks.filter(x => x.bottom >= state.offset && x.top <= bottom);

        const GridBlock: LFC<EmojiLayoutBlockData> = p =>
            <div className={`emoji-grid-${p.type}`} style={{ position: 'absolute', top: p.top, height: p.bottom - p.top }}>
                {p.children}
            </div>;

        const GridItem: LFC<{ id: string }> = ({ id }) =>
            <div className="emoji-grid-row-item" style={cellStyling} onClick={this.onEmojiSelect.bind(this, id)} onMouseEnter={this.onEmojiPreview.bind(this, id)}>
                <Emoji idOrCode={id} />
            </div>;

        return <div className="emoji-picker">
            <div className="emoji-category-list">
                {categories.map(x =>
                    <div key={x.id} className={'emoji-category-list-item ' + (x.id === category ? '--active' : '')}>
                        <div className={'emoji-category-' + x.id}
                            onClick={() => this.onCategoryClick(x.id)} />
                    </div>
                )}
            </div>
            <div className="emoji-search">
                <input type="text" placeholder="Search" value={this.state.search || ''} onChange={e => this.onSearch(e.currentTarget.value)} />
            </div>
            <div className="emoji-options">
                {diversities.map(x =>
                    <div key={x.id}
                        className={cl('emoji-diversity', `emoji-diversity-${x.code || 'default'}`, [x.code === state.diversity, '--active'])}
                        onClick={() => this.onDiversitySelect(x.code)}
                    />
                )}
            </div>
            <XScrollbars ref={e => this.scrollbars = e} onScroll={() => this.onScroll()} style={scrollerStyling}>
                <div className="emoji-grid" style={gridStyling}>
                    {blocks.map(({ key, ...x }) =>
                        <GridBlock key={key} {...x}>
                            {x.type === 'heading' && <span>{x.label}</span>}
                            {x.type === 'row' && x.items.map(e => <GridItem key={e.id} id={e.id} />)}
                        </GridBlock>
                    )}
                </div>
            </XScrollbars>
            <EmojiPreview idOrCode={state.preview} width={this.props.width!} />
        </div>;
    }

    public componentDidMount = () => this.onScroll();

    private onCategoryClick(category: string): void {
        const block = this.derivedState.layout.blocks
            .find(x => x.type === 'heading' && x.key === category);
        if (block) {
            this.scrollbars!.scrollTop(block.top); // This will rerender
        }
    }

    private onScroll(): void {
        this.setState({ offset: this.scrollbars!.getScrollTop() });
    }

    private onSearch(search: string): void {
        this.setState({ search });
    }

    private onEmojiPreview(emojiId: string): void {
        this.setState({ preview: emojiId || undefined });
    }

    private onEmojiSelect(emoji: string): void {
        this.props.onSelect(EmojiStore.find(emoji));
    }

    private onDiversitySelect(diversity: string): void {
        this.props.diversityHandler!.set(diversity);
        this.setState({ diversity });
    }
}

const EmojiPreview: React.FC<{ idOrCode?: string, width: number }> = ({ idOrCode, width }) => {
    const emoji = EmojiStore.find(idOrCode ?? '');
    return <div className="emoji-preview" style={{ width }} title={!!emoji ? emoji.name : ''}>
        {emoji && <>
            <Emoji idOrCode={emoji.id} inline /> {emoji.name}
        </>}
    </div>;
};

type EmojiGridSet = {
    key: string,
    label: string,
    items: EmojiData[],
};

type EmojiGridDimensions = {
    width: number,
    height: number,
    margin: number,
    headingHeight: number,
    rowHeight: number,
    cellCount: number,
    cellSpacing: number,
};

function computeDimensions(width: number, height: number): EmojiGridDimensions {
    return {
        width,
        height,
        margin: 3,
        headingHeight: 38,
        rowHeight: 32,
        cellCount: 8,
        cellSpacing: 4,
    };
}

function computeContents(diversity?: string, query?: string): EmojiGridSet[] {

    diversity = diversity || EmojiDiversifications[0].code;
    query = (query || '').trim().toLowerCase();

    let es = EmojiStore.getEmoji();
    const cs = EmojiStore.getCategories();

    // Reduce...
    es = es.filter(x => !x.canDiversify || (!!diversity ? x.diversity?.includes(diversity) : !x.diversity?.length));

    if (query) {
        es = es.filter(x =>
            x.keywords.some(w => w.startsWith(query)) ||
            x.texts.some(w => w.startsWith(query))
        );
    }

    // Group...
    const escs = es.reduce((r: any, v, i, a, k = v.category) => ((r[k] || (r[k] = [])).push(v), r), {});

    // Assemble...
    const hs = cs
        .map(x => ({
            key: x.id,
            label: x.label,
            items: escs[x.id] || [],
        }))
        .filter(x => !!x.items.length);

    return hs;
}

function computeLayout(contents: EmojiGridSet[], dims: EmojiGridDimensions): EmojiLayout {
    return EmojiLayout.compute(contents, dims);
}

function computeCategory(offset: number, layout: EmojiLayout): string {
    let category = '';
    let top = 0;
    for (const bl of layout.blocks) {
        if (bl.type === 'heading') {
            if (bl.top <= offset) {
                if (!top || top < bl.top) {
                    top = bl.top;
                    category = bl.key;
                }
            }
        }
    }
    return category;
}

type EmojiLayoutBlockData = { type: 'heading' | 'row', key: string, top: number, bottom: number };
type EmojiLayoutHeadingData = EmojiLayoutBlockData & { type: 'heading', label: string };
type EmojiLayoutItemsData = EmojiLayoutBlockData & { type: 'row', items: EmojiData[] };
type EmojiLayoutBlockTypes = EmojiLayoutHeadingData | EmojiLayoutItemsData;

class EmojiLayout {

    private static keySeed: number = 0;

    public blocks!: EmojiLayoutBlockTypes[];
    public width!: number;
    public height!: number;
    public cellWidth!: number;
    public cellMargin!: number;
    public cellPadding!: number;

    public static compute(contents: EmojiGridSet[], dims: EmojiGridDimensions): EmojiLayout {

        contents = contents.filter(x => !!x.items.length);

        const cellCount = dims.cellCount;
        const cellWidth = Math.floor(((dims.width - (dims.margin * 2)) - (dims.cellSpacing * (cellCount + 1))) / dims.cellCount);
        const cellMargin = dims.cellSpacing / 2;
        const cellPadding = (cellWidth - 24) / 2;

        const height = contents
            .filter(x => !!x.items.length)
            .map(x => dims.headingHeight + (dims.rowHeight * Math.floor(x.items.length / dims.cellCount)))
            .reduce((t, x) => t + x, 0);

        const rows = [] as EmojiLayoutBlockTypes[];

        const key = () => `k${(EmojiLayout.keySeed++)}`;
        let top = 0;

        for (const es of contents) {
            rows.push({ type: 'heading', key: es.key, top, bottom: top + dims.headingHeight, label: es.label });
            top += dims.headingHeight;
            for (let i = 0; i < es.items.length; i += cellCount) {
                rows.push({ type: 'row', key: key(), top, bottom: top + dims.rowHeight, items: es.items.slice(i, i + cellCount) });
                top += dims.rowHeight;
            }
        }

        const el = new EmojiLayout();
        el.width = dims.width;
        el.height = height;
        el.cellWidth = cellWidth;
        el.cellMargin = cellMargin;
        el.cellPadding = cellPadding;
        el.blocks = rows;
        return el;
    }
}

function cl(...input: any[]) {
    input = input.map(x => Array.isArray(x) ? (!!x[0] ? x[1] : '') : x);
    return input.join(' ');
}
